import type { ClientPortalMessage as ExistingMessage } from '@einwert/valuation-api-client'

import { Box, Collapse, Stack, styled, useTheme } from '@mui/material'
import { Alert, ContentChip, Message, ParticipantsDrawer, TinyButton, tokens, UploadErrorAlert, User } from 'ComponentLibrary'
import { TextEditor } from 'ComponentLibrary/src/molecules/TextEditor/TextEditor'
import { isEmpty } from 'lodash'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useMessageListData } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageList.data'
import invariant from 'tiny-invariant'
import { getIsMessageAggregated } from 'ComponentLibrary/src/widgets/ThreadListPreview/getIsMessageAggregated'
import { ThreadWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import { BellSlashIcon, CaretDownIcon, CaretUpIcon, EnvelopeIcon, UserCircleDashedIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { type ExternalParticipant } from '@einwert/valuation-api-client'
import { Typography } from 'ComponentLibrary/src/piazza'
import { MessageMetadata } from 'ComponentLibrary/src/widgets/MessageMetadata'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type ThreadDetailsProps = {
    thread: ThreadWithMetadata
    messagesContainerHeightBuffer: string
    headerComponent: ReactNode
    threadCreatorComponent?: ReactNode
    isEditingParticipants: boolean
    onEditParticipants: (value: boolean) => void
    orderObservers: User[]
    externalParticipantUsers: ExternalParticipant[]
    orderLabel: string
    externalParticipants?: ExternalParticipant[]
    isExternalParticipantsPending?: boolean
} & Omit<
    ReturnType<typeof useMessageListData>,
    'pinThread' | 'unpinThread' | 'isPatchingPin' | 'deleteThread' | 'isDeleteThreadPending' | 'orderOverview'
>

// TODO: Relocate to ComponentLibrary - component used across multiple features
export function MessageList({
    thread,
    messagesContainerHeightBuffer,
    headerComponent,
    threadCreatorComponent,
    isEditingParticipants,
    onEditParticipants,
    currentUser,
    replaceParticipantIds,
    isPatchingThread,
    createMessage,
    updateMessage,
    deleteMessage,
    uploadMessageDocuments,
    failedDocumentUploads,
    downloadDocument,
    users,
    order,
    orderObservers,
    externalParticipantUsers,
    orderLabel,
    isExternalParticipantsPending = false,
    externalParticipants = [],
}: ThreadDetailsProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const userLinkCopy = useUserLinkCopy()
    const locale = useCurrentResolvedLocale()
    const theme = useTheme()
    const navigate = useNavigate()
    const containerRef = useRef<HTMLDivElement>(null)
    const endOfListRef = useRef<HTMLDivElement>(null)

    const messages = thread.messages

    const stringifiedMessages = JSON.stringify(thread.messages)

    useEffect(() => {
        endOfListRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [stringifiedMessages])

    const onSubmitMessage = ({ message, documents, mentions }: { message: string; documents: File[]; mentions: number[] }) => {
        invariant(currentUser, 'User must be defined')
        createMessage(
            { content: message, user: currentUser, mentions },
            { onSuccess: ({ uuid: messageId }) => uploadMessageDocuments(documents.map((document) => ({ messageId, document }))) }
        )
    }

    const hasOtherOrderObservers = !!orderObservers?.some((observer) => observer.uuid !== currentUser?.uuid)
    const hasExternalParticipants = externalParticipantUsers.length > 0

    const navigateToObserversPage = () => {
        if (order?.uuid) {
            void navigate(`/orders/${order.uuid}/overview?edit=true`)
        }
    }

    const [isEmailDetailsExpanded, setEmailDetailsExpanded] = useState(false)
    const handleExpandEmailDetails = () => {
        setEmailDetailsExpanded(() => !isEmailDetailsExpanded)
    }

    const isOrderWithExternalParticipants = externalParticipants.length > 0
    const textEditorCopy = {
        noMentionResults: t('textEditor.noMentionResults'),
        placeholder: t('textEditor.placeholder'),
        attachmentTooLarge: t('textEditor.attachmentTooLarge'),
        toolbar: {
            textStyle: t('textEditor.toolbar.textStyle'),
            bold: t('textEditor.toolbar.bold'),
            italic: t('textEditor.toolbar.italic'),
            bulletedList: t('textEditor.toolbar.bulletedList'),
            numberedList: t('textEditor.toolbar.numberedList'),
            headingOptions: {
                paragraph: t('textEditor.toolbar.headingOptions.paragraph'),
                h2: t('textEditor.toolbar.headingOptions.h2'),
                h3: t('textEditor.toolbar.headingOptions.h3'),
                h4: t('textEditor.toolbar.headingOptions.h4'),
                h5: t('textEditor.toolbar.headingOptions.h5'),
            },
        },
    }
    const messageCopy = {
        deleteTitle: t('message.deleteTitle'),
        deleteDialogContent: t('message.deleteDialogContent'),
        deleteConfirm: t('message.deleteConfirm'),
        edit: t('message.edit'),
        delete: t('message.delete'),
        edited: t('message.edited'),
        attachments: t('message.attachments'),
        deletedMessage: t('message.deletedMessage'),
        system: t('message.system'),
        textEditor: textEditorCopy,
        userLink: userLinkCopy,
    }
    const participantsDrawerCopy = {
        title: t('participantsDrawer.title'),
        addParticipantsLabel: t('participantsDrawer.addParticipantsLabel'),
        notifyMe: t('participantsDrawer.notifyMe'),
        participantCount: (count: number) => t('participantsDrawer.participantCount', { count }),
        observerCount: (count: number) => t('participantsDrawer.observerCount', { count }),
        editObservers: t('participantsDrawer.editObservers'),
        observerInfo: t('participantsDrawer.observerInfo'),
        responsible: t('participantsDrawer.responsible'),
    }

    return (
        <Stack direction="column" width="100%" overflow="hidden" position="relative" ref={containerRef}>
            {headerComponent}
            <Stack
                direction="column"
                height="100%"
                sx={{ containerType: 'inline-size' }}
                maxHeight={`calc(100vh - (${messagesContainerHeightBuffer}))`}
            >
                <MessagesContainer gap={theme.spacing(4)}>
                    {threadCreatorComponent}
                    {messages.map((message: ExistingMessage, index) => {
                        const isAggregated = getIsMessageAggregated({ messages, index })
                        return (
                            <Box
                                key={`message-${message.uuid}`}
                                bgcolor={tokens.ColorSurfaceDefault}
                                padding="10px"
                                width={'min(90%, 640px)'}
                                borderRadius="4px"
                                marginTop={isAggregated ? theme.spacing(-2.5) : '0px'}
                            >
                                <Message
                                    currentUserId={currentUser?.id}
                                    message={message}
                                    deleteMessage={deleteMessage}
                                    updateMessage={updateMessage}
                                    isAggregated={isAggregated}
                                    downloadDocument={downloadDocument}
                                    mentionOptions={users}
                                    externalParticipantsUsers={externalParticipantUsers}
                                    copy={messageCopy}
                                    locale={locale}
                                />
                            </Box>
                        )
                    })}
                    {currentUser && !thread.participant_ids.includes(currentUser.id) && (
                        <Alert
                            severity="info"
                            size="small"
                            icon={<BellSlashIcon />}
                            sx={{
                                margin: `${tokens.SizingSpacing150} ${tokens.SizingSpacing300}`,
                            }}
                        >
                            {t('alerts.notNotified')}
                        </Alert>
                    )}
                    {currentUser &&
                        thread.participant_ids.length === 1 &&
                        thread.participant_ids.includes(currentUser.id) &&
                        !hasOtherOrderObservers &&
                        !hasExternalParticipants && (
                            <Alert severity="warning" size="small" icon={<UserCircleDashedIcon />} sx={{ margin: tokens.SizingSpacing150 }}>
                                {t('alerts.aloneStart')}{' '}
                                <ClickableText onClick={() => onEditParticipants(true)}>{t('alerts.here')}</ClickableText>{' '}
                                {t('alerts.aloneEnd')}
                            </Alert>
                        )}
                    <div ref={endOfListRef} />
                </MessagesContainer>
                {isOrderWithExternalParticipants && (
                    <EmailDetailsComponent
                        currentUser={currentUser}
                        externalParticipants={externalParticipants}
                        handleExpandEmailDetails={handleExpandEmailDetails}
                        isEmailDetailsExpanded={isEmailDetailsExpanded}
                        order={order}
                        subject={thread.topic || t('subjectForExternalCollaboration', { orderLabel })}
                    />
                )}
                <MessageInputGrid>
                    {!isEmpty(failedDocumentUploads) && <UploadErrorAlert failedUploads={failedDocumentUploads} />}
                    <TextEditor
                        onSubmit={onSubmitMessage}
                        dragAreaRef={containerRef}
                        submitVariant="inline"
                        threadId={thread.uuid}
                        key={thread.uuid}
                        mentionOptions={users}
                        enableHeadings
                        enableNumberedList
                        validateTotalAttachmentSize={isOrderWithExternalParticipants}
                        disableSubmit={isExternalParticipantsPending}
                        copy={textEditorCopy}
                    />
                </MessageInputGrid>
            </Stack>
            <ParticipantsDrawer
                currentUser={currentUser}
                users={users}
                isEditParticipants={isEditingParticipants}
                setEditParticipants={onEditParticipants}
                participants={thread.participant_users}
                observerUsers={orderObservers || []}
                externalParticipantUsers={externalParticipantUsers}
                persistParticipants={replaceParticipantIds}
                isPersistingParticipants={isPatchingThread}
                emptyParticipantsMessage={t('alerts.emptyParticipants')}
                orderAssignedCustomerUsers={order?.assigned_customer_users}
                orderAssignedAppraiserId={order?.appraiser_assignment?.main}
                navigateToObserversPage={navigateToObserversPage}
                copy={participantsDrawerCopy}
                userLinkCopy={userLinkCopy}
            />
        </Stack>
    )
}

const MessagesContainer = styled(Stack)`
    background-color: rgb(247, 247, 247);
    height: 100%;
    overflow: auto;
    align-items: center;
    padding: 15px 0;
    width: 100%;
    z-index: 1;
`

const MessageInputGrid = styled(Stack)`
    position: sticky;
    bottom: 0;
    z-index: 2;
    background-color: white;
    padding: ${tokens.SizingSpacing300};
    @container (min-width: 600px) {
        padding: 20px calc(20% + 15px) 25px 20%;
    }
    @container (min-width: 950px) {
        padding: 20px calc(25% + 15px) 25px 25%;
    }
    @container (min-width: 1300px) {
        padding: 20px calc(30% + 15px) 25px 30%;
    }
    display: flex;
    justify-self: center;
    width: 100%;
    border-top: 1px solid ${tokens.ColorBorderDefault};
`

const ClickableText = styled(Box)(() => ({
    display: 'inline',
    textDecoration: 'underline',
    cursor: 'pointer',
    color: tokens.ColorTextLinkDefault,
    '&:hover': {
        color: tokens.ColorTextLinkHover,
    },
}))

type EmailDetailsComponentProps = Pick<ThreadDetailsProps, 'currentUser' | 'order'> & {
    handleExpandEmailDetails: () => void
    isEmailDetailsExpanded: boolean
    externalParticipants: NonNullable<ThreadDetailsProps['externalParticipants']>
    subject: string
}

const EmailDetailsComponent = ({
    handleExpandEmailDetails,
    isEmailDetailsExpanded,
    currentUser,
    order,
    externalParticipants,
    subject,
}: EmailDetailsComponentProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const externalParticipant = externalParticipants[0]
    const senderLabel = formatEmailAddress({
        name: [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' '),
        email: order?.external_participant_mailbox?.proxy_email,
        notAvailableLabel: t('notAvailable'),
    })
    const recipientLabel = formatEmailAddress({
        name: [externalParticipant?.first_name || externalParticipant?.salutation, externalParticipant?.last_name]
            .filter(Boolean)
            .join(' '),
        email: externalParticipant?.email,
        notAvailableLabel: t('notAvailable'),
    })
    const messageMetadataCopy = {
        sender: t('externalEmail.messageMetadata.sender'),
        recipient: t('externalEmail.messageMetadata.recipient'),
        subject: t('externalEmail.messageMetadata.subject'),
    }

    return (
        <Stack
            paddingY={tokens.SizingSpacing150}
            paddingX={tokens.SizingSpacing300}
            gap={tokens.SizingSpacing150}
            width={'100%'}
            justifyContent={'center'}
            alignItems={'center'}
            borderTop={`1px solid ${tokens.ColorBorderDefault}`}
        >
            <Stack maxWidth={'728px'} direction={'row'} width={'100%'} alignItems={'center'} gap={tokens.SizingSpacing100}>
                <Box width={'fit-content'} flexShrink={0}>
                    <ContentChip size="medium" color="turquoise" label={t('external')} icon={<EnvelopeIcon />} />
                </Box>
                <Box flex={1}>
                    <Typography variant="textXS" color={tokens.ColorTextDefault}>
                        {t('externalEmail.info')}
                        <br />
                        {t('externalEmail.infoSecondLine')}
                    </Typography>
                </Box>
                <Box sx={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <TinyButton
                        onClick={handleExpandEmailDetails}
                        rightIcon={isEmailDetailsExpanded ? <CaretDownIcon /> : <CaretUpIcon />}
                        label={t('externalEmail.details')}
                    />
                </Box>
            </Stack>
            <Collapse in={isEmailDetailsExpanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                <Box width="100%" maxWidth={'728px'} alignSelf="center" marginX="auto" marginTop={tokens.SizingSpacing150}>
                    <MessageMetadata sender={senderLabel} recipient={recipientLabel} subject={subject} copy={messageMetadataCopy} />
                </Box>
            </Collapse>
        </Stack>
    )
}

function formatEmailAddress({ name, email, notAvailableLabel }: { name?: string; email?: string; notAvailableLabel: string }) {
    if (!email) {
        return name || notAvailableLabel
    }

    return name ? `${name} <${email}>` : email
}
